import { Utils } from '../../../../utils/common'
import { DOMUtils } from '../../../../utils/dom'
import { SimpleEvents } from '../../../../utils/events'
import { IdentityEvent, IIdentityEvent } from '../../../common/events/identity'
import { IProfile } from '../../../common/profile'
import { IIdentityProviderConfig, IIdentityServiceAdapter, SSOScreen } from '../adapter'

interface IWindow {
    janrain: any
    janrainCaptureWidgetOnLoad: () => void
    janrainWidgetOnload: () => void
    location: any
}

declare let window: IWindow

interface IJanrainHandlerResult {
    screen: string
}

export interface IJanrainConfig extends IIdentityProviderConfig {
    account: string
    application: string
    client: string,
    flowName?: string,
    /**
     * Specifies which version of the flow to load.
     * NOTE:
     *  Typically Janrain recommends using a fixed version in production,
     *  and using 'HEAD' (always points to the latest version) during development.
     *  However, we currently use 'HEAD' version in both environments.
     *  Ref: https://janrain-education-center.knowledgeowl.com/home/registration-javascript-apis-settings
     */
    flowVersion?: string,
    logout?: string
    redirect?: string
    token?: string
    xd?: string
}

export class JanrainAdapter extends SimpleEvents implements IIdentityServiceAdapter<IJanrainConfig> {
    private janrainEventHandlers: { [key: string]: (...args: any[]) => void } = {
        onCaptureContentChange: this.onContentChange,
        onCaptureLoginStart: this.onCaptureLoginStart,
        onCaptureModalReady: this.onCaptureModalReady,
        onCaptureScreenShow: this.onCaptureScreenShow,
        onCaptureValidationComplete: this.handleFormErrors,
        onCaptureValidationFailed: this.handleInputError,
        onCaptureValidationSuccess: this.clearInputError,
        onModalClose: this.modalClose,
        onModalOpen: this.modalOpen,
        onProviderLoginCancel: this.handleSocialError,
        onProviderLoginError: this.handleSocialError
    }
    private janrainBaseEventHandlers: { [key: string]: (...args: any[]) => void } = {
        onCaptureEmailVerificationSuccess: this.onEmailVerified,
        onCaptureLoginSuccess: this.onChange,
        onCaptureRegistrationSuccess: this.onChange,
        onCaptureSessionEnded: this.onChange,
        onCaptureSessionFound: this.onInit,
        onCaptureSessionNotFound: this.onInit,
    }
    private janrainJsUrl = '//widgets-cdn.rpxnow.com/manifest/capture:login'
    private janrainPreConfigured = false
    private janrainSession: Promise<boolean>
    private janrainSessionResolve: () => void

    private janrainModals = {
        emailVerificationRequired: 'emailVerificationRequired',
        verifyEmail: 'verifyEmail'
    }
    private janrainParams = {
        modal: 'screenToRender'
    }
    private janrainFocused = false
    private modalActive = false
    private refocusDelay = 300
    private shortDelay = 50
    private longDelay = 600
    private siteName = ''
    private isModalReady = false
    private showPasswordPolicy = false

    // default configuration - ideally no need to allow page config for these
    private settings = {
        appUrl: 'https://login.canada.com',
        capture: {
            federate: true,
            federateEnableSafari: true,
            keepProfileCookie: true,
            keepProfileCookieAfterLogout: true,
            modalCloseHtml: '<span class="visually-hidden">close</span>',
            noModalBorderInlineCss: true,
            noStyling: true, // override default styling
            recaptchaVersion: 2,
            registerFlow: 'socialRegistration',
            responseType: 'token',
            setProfileCookie: true, // saves a call, apparently
            setProfileData: true
        },
        // custom: true, // prevents default providers display
        packages: [
            'capture',
            'login'
        ],
        providers: [
            'facebook',
            'googleplus',
        ],
        tokenAction: 'event'
    }

    public get closeButton(): HTMLElement | null {
        return document.querySelector(`.janrain_modal_closebutton`)
    }

    public get modalContentFields(): NodeListOf<HTMLElement> | null {
        return document.querySelectorAll(`#janrainModal button, #janrainModal input:not([type="hidden"])`)
    }

    /**
     * Begins SSO session.
     */
    public enable() {
        // register event handlers we need to be aware of
        Object.keys(this.janrainBaseEventHandlers).forEach((key) =>
            window.janrain.events[key].addHandler(this.janrainBaseEventHandlers[key].bind(this)))

        // #DEV - register all available handlers
        // #Object.keys(window.janrain.events).forEach(k => {
        // #    if (window.janrain.events.hasOwnProperty(k) && window.janrain.events[k].addHandler) {
        // #        window.janrain.events[k].addHandler((...args: any[]) =>
        // #            console.log(`${k} =`, ...args))
        // #    }
        // #})

        window.janrain.capture.ui.registerFunction('passwordValidation', this.validateNewPassword)

        if (!this.janrainPreConfigured) {
            Object.keys(this.janrainEventHandlers).forEach((key) =>
                window.janrain.events[key].addHandler(this.janrainEventHandlers[key].bind(this)))
            window.janrain.capture.ui.start()
        }
    }

    /**
     * Gets profile data from Janrain SDK.
     */
    public fetch(): IProfile {
        const profileData = {
            doNotContact: undefined,
            email: undefined,
            emailConfirmed: undefined,
            gender: undefined,
            lastSignIn: undefined,
            name: undefined,
            uuid: undefined,
        } as IProfile

        try {
            const get = window.janrain.capture.ui.getProfileCookieData
            if (window.janrain.capture.ui.hasActiveSession()) {
                profileData.email = get('email')
                profileData.name = get('displayName')
                profileData.uuid = get('uuid')
                profileData.gender = this.cleanGender(get('gender'))
                // `2020-10-28 17:40:14 +0000` --> true or null --> false
                profileData.emailConfirmed = !!get('emailVerified')
                profileData.lastSignIn = get('lastLogin') ? new Date(get('lastLogin')) : undefined
                profileData.doNotContact = get('optIn') ? !(get('optIn').status) : undefined
                if (!profileData.email) {
                    throw new Error('Session active but no user data.')
                }
            }
        } catch (e) {
            console.error('Problem retrieving profile from Janrain:', e)
        }

        return profileData
    }

    public cleanGender(value: string): string {
        // Janrain is returning `--` if user profile doesn't have gender. Don't send to mMparticle in that case.
        // NOTE:
        // - React profile app doesn't allow to set gender in the setting page at the moment.
        //   Probably some users may have gender configured from some legacy services.
        // - Couldn't find the possible gender values(F/M) from the Janrain cookie.
        //   Therefore, no cleanup for the time being.
        if (!value) {
            return undefined
        } else {
            const maybeTrimmedValue = String(value).trim()
            return maybeTrimmedValue === '--' ? undefined : maybeTrimmedValue
        }
    }

    /**
     * Gets signed in/out state. Awaitable for robustness (if called before Janrain ready to report).
     */
    public async hasSession(): Promise<boolean> {
        await this.janrainSession
        return window.janrain.capture.ui.hasActiveSession()
    }

    /**
     * Sets up global object with settings, loads JS.
     *
     * @param conf
     * @returns {IIdentityServiceAdapter} for chaining
     */
    public initialize(janrainConfig: IJanrainConfig) {
        this.siteName = (janrainConfig.page && janrainConfig.page.siteName) || ''

        this.showPasswordPolicy = janrainConfig.showPasswordPolicy || false

        // create session awaitable
        this.janrainSession = new Promise((_) => this.janrainSessionResolve = _)

        /**
         * This may or may not be required in the future, but for sake of migrations,
         * we're not initializing when the global object is already present
         * (meaning the backend has presumably configured it loaded the js).
         * We still add the init handler, but don't perform configuration/initialization.
         */
        this.janrainPreConfigured = !!window.janrain
        if (this.janrainPreConfigured) {
            this.registerInitHandler()
            return this
        }

        /**
         * Currently, sso.flowVersion is not configured in Cheetah page data.
         * It will be only used for testing purposes for now in dev/stage environment
         * by changing the sso client options
         */
        const optionalFlowVersion = janrainConfig.flowVersion ? { flowVersion: janrainConfig.flowVersion } : {}

        // setup global object with settings
        window.janrain = window.janrain || { settings: {} }
        window.janrain = {
            ...window.janrain,
            ...{
                settings: {
                    ...this.settings,
                    capture: {
                        ...this.settings.capture,
                        accessTokenLifeHours: 720,
                        appId: janrainConfig.account,
                        captureServer: `https://${janrainConfig.application}.janraincapture.com`,
                        clientId: janrainConfig.client,
                        federate: 1,
                        federateEnableSafari: true,
                        federateLogoutUri: `https://${janrainConfig.application}/?sso-logout`, // conf.logout,
                        // NOTE: this expects app name to be same for capture+sso, however they can differ
                        // (as does 'Postmedia dev (iframe)'). Would prefer we/Janrain update so they're the same.
                        // federateServer: `https://postmedia-us-dev.janrainsso.com`, // NEW changed
                        federateServer: `https://${janrainConfig.application}.janrainsso.com`,
                        federateXdReceiver: `https://${janrainConfig.application}/sso-xd.html`, // conf.xd, NEW added
                        // prod websites are using `standard` flow at the moment.
                        // configure cheetah sso client_options with flowName property with 'standard_localdev' only if
                        // you want test in dev env without promoting Janrain flow to prod for any new changes
                        flowName: janrainConfig.flowName || 'standard',
                        ...optionalFlowVersion,
                        recaptchaVersion: 2, //  NEW added
                        redirectUri: `https://${janrainConfig.application}/?sso-redirect`, // conf.redirect
                        setProfileCookie: true,
                        setProfileData: true
                    }
                    // tokenUrl: `https://${ssoCreds.application}/?sso-token` // conf.token
                }
            }
        }

        // enable debugging outside prod
        // if (__NON_PROD) {
        // this causes repeated share widget load warnings that are apparently okay,
        // but clutter up the console. Enable as needed for local dev only:
        //     window.janrain.settings.debug = { enabled: true }
        // }

        // register `ready` handlers
        this.registerInitHandler()

        // load templates
        this.loadJanrainUI()
            // set ready (once templates are loaded)
            .then(() => window.janrain.ready = true)
            .catch(console.error)

        // load janrain JS
        DOMUtils.loadScript(this.janrainJsUrl)

        // make chainable
        return this
    }

    /**
     * Handles screen rendering by url parameter
     */
    public renderScreenByUrlParams() {
        // render screen function
        const paramVal = this.getUrlParamVal('screenToRender')
        if (paramVal && paramVal !== undefined) {
            const screen = paramVal[1]
            window.janrain.capture.ui.renderScreen(screen)
        }
    }

    public getUrlParamVal(param: string) {
        const field = param
        const href = window.location.href
        const reg = new RegExp('[?&]' + field + '=([^&#]*)', 'i')
        return reg.exec(href)
    }

    /**
     * Handles identity changes (sign in/out).
     */
    public onChange() {
        // close modal (typically sign-in) unless pre-conf'd or verifying
        if (!this.janrainPreConfigured && Utils.params(this.janrainParams.modal) !== this.janrainModals.verifyEmail) {
            window.janrain.capture.ui.modal.close()
        }
        this.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, this.fetch()))
    }

    /**
     * Event for content change in janrain modal
     */
    public onContentChange() {
        this.removeInlineStyles()
        this.addModalA11y()
        this.changeSocialLoginText()
        // tslint:disable-next-line: no-useless-cast
        const mergeButton = document.querySelector('#janrainModal .capture_mergeAccounts_createButton') as HTMLElement
        if (mergeButton) {
            mergeButton.style.display = 'none'
        }

        if (this.modalActive && !this.janrainFocused) {
            this.setModalContentFocus()
        }

        if (this.shouldShowPasswordPolicy()) {
            const passwordPolicy = document.querySelector('#password_policy_message') as HTMLElement
            passwordPolicy.style.display = 'block'
        }
    }

    public onCaptureScreenShow(result: IJanrainHandlerResult) {
        const screen = result.screen
        if (screen === this.janrainModals.emailVerificationRequired) {
            document.querySelector('#janrainModal label').innerHTML = 'Your Email Address:'
            document.querySelector('#janrainModal input[type="email"]').setAttribute('readonly', 'readonly')
        }
    }

    public modalOpen() {
        this.janrainFocused = false
        this.modalActive = true

        document.querySelector('#janrainModal > section').removeAttribute('hidden')

        // create a focus trap
        if (this.closeButton) {
            this.closeButton.addEventListener('keydown',
                (e: KeyboardEvent) => this.handleLastTabEvent(e))
            this.closeButton.addEventListener('mouseup', this.handleCloseEvent)
        }
        if (this.modalContentFields.length) {
            this.modalContentFields[0].addEventListener('keydown',
                (e: KeyboardEvent) => this.handleFirstTabEvent(e))
        }
        document.addEventListener('keydown',
            (e: KeyboardEvent) => this.handleEscapeEvent(e))
    }

    public modalClose() {
        this.janrainFocused = false
        this.modalActive = false

        this.setButtonFocus()
        // remove inline styles added by Janrain on body tag when modal opens
        document.body.removeAttribute('style')
    }

    public setModalContentFocus() {
        if (this.modalContentFields.length) {
            this.janrainFocused = true
            this.modalContentFields[0].focus()
        }
    }

    /**
     * return focus to area that triggers opening the dialogs
     */
    public setButtonFocus() {
        // set focus to first one allowed focus
        setTimeout(() => {
            const buttons: NodeListOf<HTMLElement> = document.querySelectorAll(`.sso-btns button:not([tabindex^="-"])`)
            if (buttons.length) {
                buttons[0].focus()
            }
        }, this.longDelay)
    }

    /**
     * when closing the social login window, inline styles get rebuilt
     * whack-a-mole them away again running removeInlineStyles
     */
    public handleSocialError() {
        setTimeout(() => {
            this.removeInlineStyles()
            this.changeSocialLoginText()
        }, this.shortDelay)
    }

    /**
     * handle escape key navigation
     */
    public handleEscapeEvent(e: KeyboardEvent) {
        // event handle "esc" to close
        if (e.key === 'Esc' || e.key === 'Escape') {
            window.janrain.capture.ui.modal.close()
        }
    }

    /**
     * handle close button navigation
     */
    public handleCloseEvent() {
        // event handle "click" to close
        window.janrain.capture.ui.modal.close()
    }

    /**
     * handle the last el tab key navigation
     * if the user presses tab at the end of the modal
     * send them to the beginning (first input)
     */
    public handleLastTabEvent(e: KeyboardEvent) {
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            this.setModalContentFocus()
        }
    }

    /**
     * handle the first el shift tab key navigation
     * if the user presses shift+tab at the beginning of the modal
     * send them to the end (close button)
     */
    public handleFirstTabEvent(e: KeyboardEvent) {
        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault()
            this.closeButton.focus()
        }
    }

    /**
     * when input is invalid mark it so for the screen reader to tell the user
     */
    public handleInputError(args: any) {
        const input = document.getElementById(args.field.id) as HTMLInputElement
        if (input && args.message.length) {
            input.setCustomValidity(args.message)
        }
    }

    /**
     * when input is valid mark it so to clear previous invalid information
     */
    public clearInputError(args: any) {
        const input = document.getElementById(args.field.id) as HTMLInputElement
        if (input) {
            input.setCustomValidity('')
        }
    }

    /**
     * when user submits, if there are errors send focus to the first
     */
    public handleFormErrors(args: any) {
        if (args.fieldsWithError.length) {
            setTimeout(() => {
                this.janrainFocused = true
                args.fieldsWithError[0].focus()
            }, this.refocusDelay)
        }
    }

    /**
     * Remove all inline styles
     */
    public removeInlineStyles() {
        DOMUtils.nodes(document.querySelectorAll(`#janrainModal [style]`))
            .forEach((el: Element) => {
                el.removeAttribute('style')
            })
    }

    /**
     * Add/Remove different attributes to help with accessibility
     * A11y Also relies on: handleInputError, clearInputError, handleFormErrors
     * handleEscapeEvent, handleLastTabEvent, handleFirstTabEvent
     */
    public addModalA11y() {
        // remove tabindex numbers from social provider links
        DOMUtils.nodes(document.querySelectorAll(`#janrainModal .providers a`))
            .forEach((el: Element) => {
                DOMUtils.removeTabIndex(el)
            })

        // add aria-live to the janrain error divs so they get read by the screen reader
        // "polite" = read when user is idle (no interruption)
        DOMUtils.nodes(document.querySelectorAll(`#janrainModal .capture_tip_error`))
            .forEach((el: Element) => {
                el.setAttribute('aria-live', 'polite')
            })

    }

    /**
     * Change the Social Login button text
     */
    public changeSocialLoginText() {
        if (!!document.querySelector('#janrainModal .providers')) {
            // tslint:disable: max-line-length
            document.querySelector('#janrainModal .janrain-provider-text-color-facebook').innerHTML = 'Continue with Facebook'
            document.querySelector('#janrainModal .janrain-provider-text-color-googleplus').innerHTML = 'Continue with Google'
        }
    }

    /**
     * Validate password to ensure 12 characters minimum, a symbol and alphanumeric characters. Numbers allowed but optional.
     * Parameters starting with _ are ignored by linter. _ parameter required in signature by janrain
     */
    public validateNewPassword(_: string, password: string) {
        const validPasswordRegex = /^(?=.*[!@#$%^&*-])[a-zA-Z0-9!-\s@#$%^&*]{12,}$/
        return validPasswordRegex.test(password)
    }

    /**
     * Determine the correct tabindex for which buttons are supposed to be active
     * using class that gets switched by janrain
     */
    public handleBtnA11y() {
        // if signed out don't allow user to tab to sign out button
        const signOutDisable = document.querySelector('.sso-signedOut .signout-button button')
        if (signOutDisable) {
            DOMUtils.hideTabIndex(signOutDisable)
        } else {
            const signOutBtn = document.querySelector('.signout-button button')
            DOMUtils.removeTabIndex(signOutBtn)
        }

        // if signed in don't allow user to tab to sign in or subscribe button
        const signInDisable = document.querySelector('.sso-signedIn .signin-button button')
        if (signInDisable) {
            DOMUtils.hideTabIndex(signInDisable)
        } else {
            const signInBtn = document.querySelector('.signin-button button')
            DOMUtils.removeTabIndex(signInBtn)
        }

    }

    /**
     * Remove !important from inline janrain html
     */
    public stripInlineOverride() {
        DOMUtils.nodes(document.querySelectorAll(`#janrainModal [style]`))
            .forEach((el: Element) => {
                DOMUtils.replaceStyleValues(el, '!important', '')
            })
    }

    /**
     * Handles email verification success event (after sign-up/email change).
     */
    public onEmailVerified() {
        this.dispatchEvent(Utils.event(IdentityEvent.EMAIL_VERIFIED, this.fetch()))
    }

    /**
     * Handles session init event (on page load). Dispatches `Event.detail:boolean` indicating signed in/out state.
     */
    public async onInit() {
        this.janrainSessionResolve()
        this.dispatchEvent(Utils.event<IIdentityEvent<boolean>, IdentityEvent, boolean>(
            IdentityEvent.SESSION_INIT,
            await this.hasSession()))
    }

    public onCaptureLoginStart() {
        if (!this.janrainPreConfigured && !this.isModalReady) {
            this.renderScreenByUrlParams()
        }
    }

    /**
     * This is called when Janrain modal is ready.
     */
    public onCaptureModalReady() {
        this.isModalReady = true

        this.dispatchEvent(Utils.event(IdentityEvent.LOGIN_MODAL_READY))
    }

    /**
     * Registers handlers for init events. Separate method for overriding via traits.
     *
     * NOTE: this preserves existing handler for sake of migration, but as 'existing' suggests,
     * it must already be set. Setting the handler after here will prevent this from registering
     * required listeners.
     */
    public registerInitHandler() {
        const existingHandler = window.janrainCaptureWidgetOnLoad
        window.janrainCaptureWidgetOnLoad = () => {
            this.enable()
            if (existingHandler) {
                existingHandler()
            }
        }
        // dangerous now that accommodating pre-configured
        // window.janrainWidgetOnload = () => undefined // this just to suppress a warning
    }

    /**
     * Opens sign-in modal.
     */
    public signIn() {
        window.janrain.capture.ui.renderScreen(SSOScreen.SIGN_IN)
    }

    /**
     * Ends SSO session.
     */
    public signOut() {
        window.janrain.capture.ui.endCaptureSession()
        window.location.reload()
    }

    /**
     * Close modal
     */
    public closeModal() {
        window.janrain.capture.ui.modal.close()
    }

    /**
     * Fires click detected function
     * @param e
     */
    public eventFunction(e: string) {
        console.log('Event: ', e)
    }

    /**
     * public helper for readability and testing
     */
    public shouldShowPasswordPolicy() {
        return this.showPasswordPolicy
    }

    /**
     * Loads Janrain UI elements
     */
    protected async loadJanrainUI() {
        await DOMUtils.ready

        // add UI to DOM
        import('./modules/screens').then((mod) => {
            mod.JanrainScreens.initDOM()
        }).catch((e) => {
            console.error('FEM.Identity.Janrain unable to load template screens', e)
        })

        // tslint:disable-next-line: no-floating-promises
        import('./modules/buttons').then((mod) => {
            mod.JanrainButtons.initDOM(this.siteName)
        }).catch((e) => {
            console.error('FEM.Identity.Janrain unable to load buttons', e)
        })
    }

}
